// <pfs-banner-host>: Singleton Top-of-Page Banner-Stack. Hört auf
// `pfs:banner`-Events (composed:true, von beliebiger Stelle dispatcht)
// und rendert mehrere Banner untereinander. Analog <pfs-toast-host>.
//
// PFS-430 — Pascal-Direktive 2026-05-21:
//   Generic banner-system für viele Use-Cases:
//     - Session-abgelaufen (mit Countdown + Login-Button)
//     - Wartungsfenster-Ankündigung (nur Info-Text, kein Button)
//     - Service-Outage-Hinweis (Error-Variant, dismissible)
//   Mehrere parallel sichtbar (stacked) bei Bedarf.
//
// Konsumer-API:
//
//   window.dispatchEvent(new CustomEvent("pfs:banner", {
//     detail: {
//       id: "session-expired",         // optional, default: random.
//                                        // Wenn id schon existiert →
//                                        // replace.
//       title: "Sitzung abgelaufen",   // optional
//       message: "Bitte neu anmelden",  // optional
//       variant: "warning",             // "warning"|"info"|"error"|"success".
//                                        // Default "warning".
//       countdownSeconds: 10,           // 0 = kein Countdown. Default 0.
//       primary: {                      // optional Button
//         label: "Jetzt anmelden",
//         href: "/auth/login",          // optional. Bei click +
//                                        //   countdown-done: navigate.
//                                        //   Wenn fehlt: nur Event,
//                                        //   keine Navigation.
//       },
//       secondary: { label: "Abbrechen" },  // optional Button
//       dismissible: true,              // X-Button zum Schliessen.
//                                        // Default true.
//       autoCloseOnAction: true,        // close nach primary/secondary
//                                        // click. Default true.
//     },
//     bubbles: true,
//     composed: true,
//   }));
//
//   // Programmatic close:
//   window.dispatchEvent(new CustomEvent("pfs:banner-close", {
//     detail: { id: "session-expired" }
//   }));
//
// Events out (composed, cancelable):
//   pfs-banner:primary-clicked  { id }    — default: navigate primary.href +
//                                            close (wenn autoCloseOnAction)
//   pfs-banner:secondary-clicked { id }   — default: close
//   pfs-banner:countdown-done { id }       — default: navigate primary.href +
//                                             close
//   pfs-banner:dismissed { id, reason }    — fired AFTER banner closed
//                                            (reason: "primary"|"secondary"|
//                                             "dismiss"|"countdown"|"programmatic")

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";

export interface PfsBannerButton {
  label: string;
  href?: string;
}

export interface PfsBannerDetail {
  id?: string;
  title?: string;
  message?: string;
  variant?: "warning" | "info" | "error" | "success";
  countdownSeconds?: number;
  primary?: PfsBannerButton;
  secondary?: PfsBannerButton;
  dismissible?: boolean;
  autoCloseOnAction?: boolean;
  /**
   * PFS-580: Slot-Modus für beliebigen Inhalt. `slot: "x"` rendert im Banner ein
   * `<slot name="x">`; der Konsument projiziert dort Light-DOM (eigenes WC/Markup)
   * als Kind des Hosts: <pfs-banner-host><x slot="x"></x></pfs-banner-host>.
   * Sicher (Projektion, kein innerHTML). Stack-fähig via verschiedene Slot-Namen.
   * Tipp: persistenter Banner = KEIN countdownSeconds (+ dismissible nach Wunsch)
   * → bleibt bis dispatchBannerClose(id).
   */
  slot?: string;
}

interface BannerState {
  detail: Required<Pick<PfsBannerDetail, "id" | "variant">> & PfsBannerDetail;
  el: HTMLElement;
  timerId: ReturnType<typeof setInterval> | null;
  remaining: number;
  countdownEl: HTMLElement;
  // true solange ein aktiver Countdown läuft (Ring besitzt den Timer). De-Dup:
  // verhindert dass wiederholte gleich-id-Events (z.B. mehrere 401) den
  // Countdown resetten und der User dem Forced-Logout entkommt (PFS-430).
  countdown: boolean;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host {
  /* PFS-580: absolute statt fixed → Banner spannt über die pfs-shell-Box
     (App-Root), nicht den Viewport. Im WP-Embed läuft es damit über die
     Embed-Region statt über den WP-Header. Setzt voraus: Host in der
     Shell-Shadow + Shell ist position:relative. */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  font-family: inherit;
}
/* Vollbreite-Banner-Leiste (kein Toast-Kärtchen). Flächen + Text über
   DOKUMENT-LEVEL-Tokens (--pfs-color-surface/-text/-border/-primary), die
   — anders als die Status-Tints — am :root liegen und via [data-theme] flippen
   → der Banner folgt dem Theme-Toggle (gleiches Muster wie <pfs-toast-host>).
   Der Varianten-Akzent (Top-Streifen + Icon) nutzt den Status-fg-Token mit
   fixem Fallback; das ist nur die Akzentfarbe, nicht die Fläche. */
.banner {
  --banner-accent: var(--pfs-color-warning);
  width: 100%;
  background: var(--pfs-color-surface);
  color: var(--pfs-color-on-surface);
  border-top: 3px solid var(--banner-accent);
  border-bottom: 1px solid var(--pfs-color-outline);
  box-shadow: var(--pfs-shadow-overlay, 0 6px 18px -6px rgba(0,0,0,0.18));
  pointer-events: auto;
  animation: pfs-banner-in 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}
@media (prefers-reduced-motion: reduce) {
  .banner { animation: none; }
}
.banner[data-variant="info"]    { --banner-accent: var(--pfs-color-info); }
.banner[data-variant="error"]   { --banner-accent: var(--pfs-color-danger); }
.banner[data-variant="success"] { --banner-accent: var(--pfs-color-success); }
@keyframes pfs-banner-in {
  from { transform: translateY(-100%); }
  to   { transform: translateY(0); }
}
.banner__row {
  display: flex;
  gap: 0.875rem;
  align-items: center;
  max-width: 72rem;
  margin: 0 auto;
  padding: 0.75rem 1.25rem;
  flex-wrap: wrap;
}
.banner__icon {
  font-size: 1.2rem;
  line-height: 1;
  flex-shrink: 0;
  display: inline-flex;
  color: var(--banner-accent);
}
.banner__text {
  flex: 1;
  min-width: 12rem;
}
.banner__title {
  display: block;
  font-weight: 650;
  font-size: 0.95rem;
  letter-spacing: -0.01em;
  color: var(--pfs-color-on-surface);
}
.banner__message {
  font-size: 0.85rem;
  color: var(--pfs-color-on-surface-muted);
}
.banner__countdown {
  font-size: 0.85rem;
  color: var(--pfs-color-on-surface-subtle);
  font-variant-numeric: tabular-nums;
}
.banner__actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  align-items: center;
}
/* Countdown-Ring: Farbe = Banner-Akzent (kohärent zur Variante), Track als
   dezenter Tint davon. Größe kommt aus data-size am Element. */
.banner__countdown-ring {
  --pfs-ring-color: var(--banner-accent);
  --pfs-ring-track: color-mix(in srgb, var(--banner-accent) 22%, transparent);
  margin-right: 0.15rem;
}
${BUTTON_CSS}
.btn { white-space: nowrap; }
.banner__dismiss {
  background: transparent;
  border: none;
  color: var(--pfs-color-on-surface-subtle);
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  opacity: 0.75;
  transition: opacity 0.15s ease, background 0.15s ease;
}
.banner__dismiss:hover {
  opacity: 1;
  background: var(--pfs-color-surface-sunken);
}
`;

const ICONS: Record<NonNullable<PfsBannerDetail["variant"]>, string> = {
  warning: "⚠️",
  info: "ℹ️",
  error: "❌",
  success: "✓",
};

defineComponent({
  tagName: "pfs-banner-host",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;

    const style = document.createElement("style");
    style.textContent = STYLES;
    ctx.shadowRoot.appendChild(style);

    const list = document.createElement("div");
    ctx.shadowRoot.appendChild(list);

    // State pro Banner-ID
    const banners = new Map<string, BannerState>();

    const fire = (
      type: string,
      id: string,
      extra?: Record<string, unknown>,
    ): boolean => {
      const ev = new CustomEvent(type, {
        detail: { id, ...extra },
        bubbles: true,
        composed: true,
        cancelable: true,
      });
      return host.dispatchEvent(ev);
    };
    const closeById = (id: string, reason: string) => {
      const state = banners.get(id);
      if (!state) return;
      if (state.timerId !== null) clearInterval(state.timerId);
      state.el.remove();
      banners.delete(id);
      fire("pfs-banner:dismissed", id, { reason });
    };
    const navigate = (href?: string) => {
      if (href) window.location.assign(href);
    };

    const buildBanner = (detail: PfsBannerDetail): BannerState => {
      const id = detail.id ?? `banner-${Math.random().toString(36).slice(2, 10)}`;
      const variant = detail.variant ?? "warning";
      const dismissible = detail.dismissible !== false;
      const autoCloseOnAction = detail.autoCloseOnAction !== false;
      const countdownSeconds = detail.countdownSeconds ?? 0;

      const card = document.createElement("div");
      card.classList.add("banner");
      card.setAttribute("data-variant", variant);
      card.setAttribute("data-id", id);
      card.setAttribute("role", variant === "error" ? "alert" : "status");
      card.setAttribute("aria-live", "polite");

      const row = document.createElement("div");
      row.className = "banner__row";

      const iconEl = document.createElement("span");
      iconEl.className = "banner__icon";
      iconEl.textContent = ICONS[variant];
      iconEl.setAttribute("aria-hidden", "true");
      row.appendChild(iconEl);

      const textWrap = document.createElement("div");
      textWrap.className = "banner__text";
      if (detail.title) {
        const t = document.createElement("span");
        t.className = "banner__title";
        t.textContent = detail.title;
        textWrap.appendChild(t);
      }
      if (detail.message) {
        const m = document.createElement("span");
        m.className = "banner__message";
        m.textContent = " " + detail.message;
        textWrap.appendChild(m);
      }
      // PFS-580 Slot-Modus: beliebiger projizierter Inhalt (eigenes WC/Markup).
      if (detail.slot) {
        const s = document.createElement("slot");
        s.setAttribute("name", detail.slot);
        textWrap.appendChild(s);
      }
      // Der Countdown wird grafisch als <pfs-countdown-ring> in den Actions
      // gerendert (s. unten). countdownEl bleibt leer (BannerState-Kompat).
      const countdownEl = document.createElement("span");
      countdownEl.className = "banner__countdown";
      row.appendChild(textWrap);

      const actions = document.createElement("div");
      actions.className = "banner__actions";

      // Grafischer Countdown-Ring (seconds inside). Eigener Timer; löst via
      // pfs:countdown-done die Primary-Aktion (Logout) aus. Farbe = Banner-Akzent.
      let countdownRing: HTMLElement | null = null;
      if (countdownSeconds > 0) {
        countdownRing = document.createElement("pfs-countdown-ring");
        countdownRing.className = "banner__countdown-ring";
        countdownRing.setAttribute("data-remaining", String(countdownSeconds));
        countdownRing.setAttribute("data-format", "seconds");
        countdownRing.setAttribute("data-size", "30");
        actions.appendChild(countdownRing);
      }

      if (detail.primary) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn primary";
        const span = document.createElement("span");
        span.textContent = detail.primary.label;
        btn.appendChild(span);
        btn.addEventListener("click", () => {
          if (fire("pfs-banner:primary-clicked", id)) {
            navigate(detail.primary?.href);
            if (autoCloseOnAction) closeById(id, "primary");
          }
        });
        actions.appendChild(btn);
      }
      if (detail.secondary) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn secondary";
        btn.textContent = detail.secondary.label;
        btn.addEventListener("click", () => {
          if (fire("pfs-banner:secondary-clicked", id)) {
            if (autoCloseOnAction) closeById(id, "secondary");
          }
        });
        actions.appendChild(btn);
      }
      if (dismissible) {
        const x = document.createElement("button");
        x.type = "button";
        x.className = "banner__dismiss";
        x.setAttribute("aria-label", "Schließen");
        x.textContent = "✕";
        x.addEventListener("click", () => closeById(id, "dismiss"));
        actions.appendChild(x);
      }
      row.appendChild(actions);
      card.appendChild(row);

      const state: BannerState = {
        detail: { ...detail, id, variant },
        el: card,
        timerId: null,
        remaining: countdownSeconds,
        countdownEl,
        countdown: countdownSeconds > 0,
      };

      if (countdownSeconds > 0 && countdownRing) {
        // Der <pfs-countdown-ring> treibt den Countdown selbst (eigener Timer) und
        // feuert bei Ablauf pfs:countdown-done → dieselbe Aktion wie früher der
        // Text-Timer: countdown-done eskalieren → primary (Logout) navigieren +
        // Banner schließen. Der Ring-rAF endet beim Entfernen der Karte (cleanup).
        countdownRing.addEventListener("pfs:countdown-done", () => {
          if (fire("pfs-banner:countdown-done", id)) {
            navigate(detail.primary?.href);
            closeById(id, "countdown");
          }
        });
      }
      return state;
    };

    // Listener
    const onBanner = (ev: Event) => {
      const detail = (ev as CustomEvent<PfsBannerDetail>).detail ?? {};
      const id =
        detail.id ?? `banner-${Math.random().toString(36).slice(2, 10)}`;
      // Wenn Banner mit gleicher id existiert UND ein aktiver Countdown
      // läuft, ignore die neue Event-Dispatch. Verhindert dass z.B.
      // mehrere parallel 401-Responses den Countdown immer wieder
      // resetten und User der Forced-Redirect-Pressure entkommen kann.
      // Pascal-Direktive 2026-05-21 PFS-430.
      //
      // Replace nur wenn KEIN Timer aktiv ist (z.B. countdownSeconds=0,
      // dismissible-only Banner) — dann macht "letzter Event gewinnt"
      // Sinn für Content-Updates.
      const existing = banners.get(id);
      // Läuft für diese id schon ein Countdown (der Ring besitzt den Timer)?
      // → wiederholte Events (z.B. mehrere 401) ignorieren, NICHT neu bauen,
      // sonst resettet der Countdown und der User entkommt dem Forced-Logout.
      if (existing && existing.countdown) {
        return;
      }
      if (existing) closeById(id, "programmatic");
      const state = buildBanner({ ...detail, id });
      banners.set(id, state);
      list.appendChild(state.el);
    };
    const onBannerClose = (ev: Event) => {
      const detail = (ev as CustomEvent<{ id?: string }>).detail ?? {};
      if (detail.id) closeById(detail.id, "programmatic");
    };

    window.addEventListener("pfs:banner", onBanner);
    window.addEventListener("pfs:banner-close", onBannerClose);

    ctx.cleanup.push(() => {
      for (const [id] of banners) closeById(id, "programmatic");
      window.removeEventListener("pfs:banner", onBanner);
      window.removeEventListener("pfs:banner-close", onBannerClose);
    });
  },
});
